import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  Clock,
  DollarSign,
  User,
  Briefcase,
  TrendingUp,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import "../styles/EmployeeHistory.css";

const EmployeeHistory = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (employeeId) {
      loadHistory();
      loadEmployee();
    }
  }, [employeeId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEmployeeSalaryHistory(
        employeeId,
        user?.partnership_id || 1,
      );
      if (response.success) {
        setHistory(response.history);
      }
    } catch (error) {
      console.error("Failed to load salary history:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployee = async () => {
    try {
      const response = await apiService.getEmployee(employeeId);
      setEmployee(response.employee || response.data || response);
    } catch (error) {
      console.error("Failed to load employee:", error);
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
      "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const totalSalary = history.reduce((sum, h) => sum + (h.salary_amount || 0), 0);
  const totalHours = history.reduce(
    (sum, h) => sum + (h.total_regular_hours || 0) + (h.total_weekend_hours || 0),
    0,
  );
  const avgSalary = history.length > 0 ? totalSalary / history.length : 0;

  return (
    <div className='employee-history-container'>
      <div className='history-header'>
        <div className='header-right'>
          <button className='btn-back' onClick={() => navigate("/employees")}>
            <ArrowRight size={20} />
            חזרה לעובדים
          </button>
          <div>
            <h1>
              <Clock size={28} />
              היסטוריית משכורות
            </h1>
            {employee && (
              <p className='employee-info-subtitle'>
                <User size={16} />
                {employee.employee_name}
                {employee.type && (
                  <span className='type-badge'>{employee.type}</span>
                )}
                {employee.employee_number && (
                  <span className='emp-number'>#{employee.employee_number}</span>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className='history-summary-cards'>
        <div className='summary-card'>
          <div className='card-icon blue'>
            <Calendar size={22} />
          </div>
          <div className='card-content'>
            <div className='card-value'>{history.length}</div>
            <div className='card-label'>חודשי עבודה</div>
          </div>
        </div>
        <div className='summary-card'>
          <div className='card-icon green'>
            <DollarSign size={22} />
          </div>
          <div className='card-content'>
            <div className='card-value'>₪{totalSalary.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className='card-label'>סה״כ שכר</div>
          </div>
        </div>
        <div className='summary-card'>
          <div className='card-icon purple'>
            <TrendingUp size={22} />
          </div>
          <div className='card-content'>
            <div className='card-value'>₪{avgSalary.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className='card-label'>ממוצע חודשי</div>
          </div>
        </div>
        <div className='summary-card'>
          <div className='card-icon orange'>
            <Briefcase size={22} />
          </div>
          <div className='card-content'>
            <div className='card-value'>{totalHours.toFixed(2)}</div>
            <div className='card-label'>סה״כ שעות</div>
          </div>
        </div>
      </div>

      {/* History Table */}
      {loading ? (
        <div className='loading-state'>
          <div className='spinner'></div>
          <p>טוען היסטוריה...</p>
        </div>
      ) : history.length === 0 ? (
        <div className='empty-state'>
          <Clock size={48} />
          <h3>אין היסטוריית משכורות</h3>
          <p>טרם נוצרו רשומות היסטוריה עבור עובד זה</p>
        </div>
      ) : (
        <div className='history-table-wrapper'>
          <table className='history-table'>
            <thead>
              <tr>
                <th>חודש</th>
                <th>סוג עובד</th>
                <th>לקוח</th>
                <th>אתר</th>
                <th>שעות רגילות</th>
                <th>שעות סופ״ש</th>
                <th>תעריף</th>
                <th>תעריף סופ״ש</th>
                <th>משכורת</th>
                <th>גלובלי</th>
                <th>צורת תשלום</th>
                <th>הערות</th>
              </tr>
            </thead>
            <tbody>
              {history.map((record) => (
                <tr key={record.id}>
                  <td className='month-cell'>
                    <Calendar size={14} />
                    {formatMonth(record.month)}
                  </td>
                  <td>
                    <span className={`type-badge ${record.employee_type === 'קבלן' ? 'kablan' : record.employee_type === 'חברה' ? 'company' : 'both'}`}>
                      {record.employee_type || "-"}
                    </span>
                  </td>
                  <td>{record.customer_name || "-"}</td>
                  <td>{record.site_name || "-"}</td>
                  <td className='number-cell'>{(record.total_regular_hours || 0).toFixed(2)}</td>
                  <td className='number-cell'>{(record.total_weekend_hours || 0).toFixed(2)}</td>
                  <td className='number-cell'>₪{(record.hour_rate || 0).toFixed(2)}</td>
                  <td className='number-cell'>₪{(record.weekend_hour_rate || 0).toFixed(2)}</td>
                  <td className='salary-cell'>
                    ₪{(record.salary_amount || 0).toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td>
                    {record.is_global_salary === 'כן' ? (
                      <span className='global-badge'>כן</span>
                    ) : (
                      <span className='no-badge'>לא</span>
                    )}
                  </td>
                  <td>{record.payment_method || "-"}</td>
                  <td className='comments-cell' title={record.comments || ""}>
                    {record.comments
                      ? record.comments.length > 30
                        ? record.comments.substring(0, 30) + "..."
                        : record.comments
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmployeeHistory;
